import { AxiosResponse } from 'axios';
import { APIError } from '../types/types';
import { STORE_NUMBER_KEY } from '../constants/constants';

export const sanitizeURL = (url: string): string => {
  if (!url) {
    return '';
  }

  const badURLRegex = RegExp('^((https)|(http)):/{3,}');
  const isBadURL = badURLRegex.test(url);

  if (isBadURL) {
    return 'https://' + url.replace(badURLRegex, '');
  }

  return url;
};

export const getErrorMessage = (response?: AxiosResponse<APIError>) => {
  if (response?.status === 400 && response?.data.errors[0].code) {
    return response?.data.errors[0].message;
  }

  return null;
};

export const getSelectedStore = () =>
  sessionStorage.getItem(STORE_NUMBER_KEY) || '';

export type SearchChangeValue = string | string[];

export interface Params {
  ticketId: string;
}

/** Matches API contract for advanceSearch payload */
export interface AdvanceSearch {
  actions: string;
  subCategory: string;
  channels: string;
  category: string;
  endDate: string;
  assignmentGroup?: string[];
  channel?: string[];
  description: string;
  action?: string[];
  sendToMedalia: string;
  shortDescription: string;
  categories?: string[];
  subcategories?: string[];
  startDate: string;
}

/** Top-level search state used in parent */
export interface SearchState {
  searchValue: string;
  openedBy: string;
  advanceSearch: AdvanceSearch;
}

/** Initial state aligned with API */
export const initialSearch: SearchState = {
  searchValue: '',
  openedBy: '',
  advanceSearch: {
    endDate: '',
    description: '',
    sendToMedalia: '',
    shortDescription: '',
    startDate: '',
    category: '',
    actions: '',
    subCategory: '',
    channels: '',
  },
};

const asString = (v: SearchChangeValue) =>
  Array.isArray(v) ? v[0] ?? '' : v ?? '';

const toArray = (v: SearchChangeValue) =>
  Array.isArray(v) ? v : v ? [String(v)] : [];

/**
 * Update SearchState with a UI-level name/value and return a new state.
 * - Maps simple UI names to API’s nested advanceSearch keys.
 * - Handles arrays vs strings.
 * - Maps dates (dueDateFrom → startDate, dueDateTo → endDate).
 */
export function updateSearchState(
  prev: SearchState,
  name: string,
  value: SearchChangeValue
): SearchState {
  const next: SearchState = {
    ...prev,
    advanceSearch: { ...prev.advanceSearch },
  };

  if (name === 'searchValue') {
    next.searchValue = asString(value);
    return next;
  }
  if (name === 'openedBy') {
    next.openedBy = asString(value);
    return next;
  }

  switch (name) {
    case 'shortDescription':
      next.advanceSearch.shortDescription = asString(value);
      break;
    case 'description':
      next.advanceSearch.description = asString(value);
      break;
    case 'sendToMedalia':
      next.advanceSearch.sendToMedalia = asString(value);
      break;

    case 'dueDateFrom':
      next.advanceSearch.startDate = asString(value);
      break;
    case 'dueDateTo':
      next.advanceSearch.endDate = asString(value);
      break;

    case 'channel':
      next.advanceSearch.channels = asString(value);
      next.advanceSearch.channel = toArray(value);
      break;
    case 'action':
      next.advanceSearch.actions = asString(value);
      next.advanceSearch.action = toArray(value);
      break;
    case 'category':
      next.advanceSearch.category = asString(value);
      next.advanceSearch.categories = toArray(value);
      break;
    case 'subCategory':
      next.advanceSearch.subCategory = asString(value);
      next.advanceSearch.subcategories = toArray(value);
      break;
    case 'assignmentGroup':
      next.advanceSearch.assignmentGroup = toArray(value);
      break;

    default:
      break;
  }

  return next;
}

type StringKeys =
  | 'endDate'
  | 'description'
  | 'sendToMedalia'
  | 'shortDescription'
  | 'startDate';

type ArrayKeys =
  | 'assignmentGroup'
  | 'channel'
  | 'action'
  | 'categories'
  | 'subcategories';

function pruneAdvanceSearch(adv: AdvanceSearch): Partial<AdvanceSearch> {
  const out: Partial<AdvanceSearch> = {};

  const stringKeys: StringKeys[] = [
    'endDate',
    'description',
    'sendToMedalia',
    'shortDescription',
    'startDate',
  ];

  const arrayKeys: ArrayKeys[] = [
    'assignmentGroup',
    'channel',
    'action',
    'categories',
    'subcategories',
  ];

  stringKeys.forEach((k) => {
    const v = adv[k];
    if (typeof v === 'string' && v.trim().length > 0) {
      out[k] = v;
    }
  });

  arrayKeys.forEach((k) => {
    const v = adv[k];
    if (Array.isArray(v) && v.length > 0) {
      out[k] = v;
    }
  });

  return out;
}

// eslint-disable-next-line @typescript-eslint/ban-types
function isEmptyObject(obj: object): boolean {
  return Object.keys(obj).length === 0;
}

export function buildIncidentParamsNested(
  base: Record<string, any>,
  search: SearchState,
  extra?: {
    exportEmail?: string;
    export?: string;
    assignedTo?: string;
  }
) {
  const prunedAdvanced = pruneAdvanceSearch(search.advanceSearch);

  // Convert openedBy comma-separated string to array if it contains commas
  let processedOpenedBy: string | string[] | undefined =
    search.openedBy || undefined;
  if (processedOpenedBy && processedOpenedBy.includes(',')) {
    processedOpenedBy = processedOpenedBy
      .split(',')
      .map((v) => v.trim())
      .filter((v) => v.length > 0);
  }

  const params: Record<string, any> = {
    ...base,
    searchValue: search.searchValue || undefined,
    openedBy: processedOpenedBy,
    ...(isEmptyObject(prunedAdvanced) ? {} : { advanceSearch: prunedAdvanced }),
  };

  if (extra?.exportEmail) params.exportEmail = extra.exportEmail;
  if (extra?.export) params.export = extra.export;
  if (extra?.assignedTo) params.assignedTo = extra.assignedTo;

  return params;
}

/** Determine if any criteria are present (useful for enabling Search/Clear buttons) */
export function hasAnyCriteria(search: SearchState): boolean {
  const s = search;
  const adv = s.advanceSearch;

  const hasSearchText = (s.searchValue || '').trim().length > 0;
  const hasOpenedBy = (s.openedBy || '').trim().length > 0;
  const hasDates = !!adv.startDate || !!adv.endDate;
  const hasText =
    !!adv.shortDescription?.trim() ||
    !!adv.description?.trim() ||
    !!adv.sendToMedalia?.trim();
  const hasArrays =
    (adv.channel?.length ?? 0) > 0 ||
    (adv.action?.length ?? 0) > 0 ||
    (adv.categories?.length ?? 0) > 0 ||
    (adv.subcategories?.length ?? 0) > 0 ||
    (adv.assignmentGroup?.length ?? 0) > 0;

  return hasSearchText || hasOpenedBy || hasDates || hasText || hasArrays;
}
